import React from 'react'
import { frenchMonth } from '../../../../variables/functions';
import { LOGGED_USER } from '../../../../variables/constants';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { Table } from 'antd';
import entete from '../../../../assets/img/entete.jpg'
import axiosInstance from '../../../../variables/api/axiosInstance';

dayjs.locale('fr');

const TableauRecapitulatifRetrocession = ({ selectedGroup }) => {

    const formatNumberForExport = (value) => {
        if (!value && value !== 0) return '';
        return new Intl.NumberFormat('en-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.round(value)).replace(/\./g, ' ');
    };

    const loadImageAsBase64 = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    const fetchInsuranceLogos = async () => {
        try {
            const response = await axiosInstance.get(`/api/organisme/all/assurances`);
            return response.data || [];
        } catch (error) {
            console.error("Erreur lors de la récupération des logos:", error);
            return [];
        }
    };

    // ============================================================
    // Source de données UNIQUE utilisée partout (tableau, Excel, PDF)
    // ============================================================
    const createDataSource = (group) => {
        const compagniesArray = group?.retrocessions
            && Object.values(group?.retrocessions)
                ?.sort((a, b) => a.compagnie.code.localeCompare(b.compagnie.code, undefined, { numeric: true }));

        // --- Totaux CESSION (arrondis) ---
        const totalCessionEmissions = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.cession?.totalEmissions || 0), 0) || 0);
        const totalCessionSinistres = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.cession?.totalSinistres || 0), 0) || 0);
        const totalCessionIndemnites = Math.round(totalCessionEmissions * 0.15);
        const penalitesCession = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.penalite || 0), 0) || 0);
        const soldeCession = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.cession?.solde || 0), 0) || 0);

        // --- Totaux RETROCESSION (arrondis) ---
        const totalRetroEmissions = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.totalEmissions || 0), 0) || 0);
        const totalRetroSinistres = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.totalSinistres || 0), 0) || 0);
        const totalRetroIndemnitesGestion = Math.round(totalRetroEmissions * 0.15);
        const totalRetroFraisAdmin = Math.round(totalRetroEmissions * 0.06);
        const totalRetroPreventionRoutiere = Math.round(totalRetroEmissions * 0.01);
        const totalRetroReserveGarantie = Math.round(totalRetroEmissions * 0.05);

        // --- Calcul du solde recalculé par compagnie (comme le backend) ---
        const getCalculatedSolde = (c) => {
            const emissions = Math.round(c.totalEmissions || 0);
            const sinistres = Math.round(c.totalSinistres || 0);
            const indemnites = Math.round(emissions * 0.15);
            const frais = Math.round(emissions * 0.06);
            const prt = Math.round(emissions * 0.01);
            const rg = Math.round(emissions * 0.05);
            const penalite = Math.round(c.penalite || 0);
            const diff = Math.round(c.diffSoldeCession || 0);
            return emissions - sinistres - indemnites - frais - prt - rg - penalite + diff;
        };

        // Totaux pour les nouvelles lignes
        // const totalCalculatedSolde = Math.round(
        //     compagniesArray?.reduce((sum, c) => sum + getCalculatedSolde(c), 0) || 0);
        const totalDiffSoldeCession = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.diffSoldeCession || 0), 0) || 0);

        const dataSource = [
            // ======== Section CESSION ========
            {
                key: 'cession-header',
                description: 'CESSION',
                isHeader: true,
                isCessionSection: true
            },
            {
                key: 'cession-souscriptions',
                description: 'MONTANT TOTAL DES SOUSCRIPTIONS',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(c.cession?.totalEmissions || 0);
                    return acc;
                }, {}),
                total: formatNumberForExport(totalCessionEmissions),
                isCessionSection: true
            },
            {
                key: 'cession-sinistres',
                description: 'SINISTRES PAYES',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.cession?.totalSinistres || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalCessionSinistres),
                isCessionSection: true
            },
            {
                key: 'cession-indemnites',
                description: 'INDEMNITES DE GESTION 15%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const indemnite = Math.round((c.cession?.totalEmissions || 0) * 0.15);
                    acc[`compagnie${index}`] = formatNumberForExport(-indemnite);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalCessionIndemnites),
                isCessionSection: true
            },
            {
                key: 'cession-solde',
                description: 'Solde de Cession ---->',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(c.cession?.solde);
                    return acc;
                }, {}),
                total: formatNumberForExport(soldeCession),
                isSolde: true,
                isCessionSection: true
            },
            // ======== Section RETROCESSION ========
            {
                key: 'retro-header',
                description: 'RETROCESSION',
                isHeader: true,
                isRetroSection: true
            },
            {
                key: 'retro-emission',
                description: 'MONTANT EMISSION NETTE RETRO',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(c.totalEmissions || 0);
                    return acc;
                }, {}),
                total: formatNumberForExport(totalRetroEmissions),
                isRetroSection: true
            },
            {
                key: 'retro-sinistres',
                description: 'SINISTRES REGLES RETRO',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.totalSinistres || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroSinistres),
                isRetroSection: true
            },
            {
                key: 'retro-indemnite',
                description: 'INDEMNITE GESTION SUR CA RETRO 15%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const indemnite = Math.round((c.totalEmissions || 0) * 0.15);
                    acc[`compagnie${index}`] = formatNumberForExport(-indemnite);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroIndemnitesGestion),
                isRetroSection: true
            },
            {
                key: 'retro-frais',
                description: 'FRAIS ADMINISTRATIFS DU POOL 6%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const frais = Math.round((c.totalEmissions || 0) * 0.06);
                    acc[`compagnie${index}`] = formatNumberForExport(-frais);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroFraisAdmin),
                isRetroSection: true
            },
            {
                key: 'retro-prevention',
                description: 'PREVENTION ROUTIERE TOGOLAISE 1%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const prevention = Math.round((c.totalEmissions || 0) * 0.01);
                    acc[`compagnie${index}`] = formatNumberForExport(-prevention);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroPreventionRoutiere),
                isRetroSection: true
            },
            {
                key: 'retro-reserve',
                description: 'RESERVE DE GARANTIE 5%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const reserve = Math.round((c.totalEmissions || 0) * 0.05);
                    acc[`compagnie${index}`] = formatNumberForExport(-reserve);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroReserveGarantie),
                isRetroSection: true
            },
            penalitesCession > 0 ? {
                key: 'penalites',
                description: 'PENALITES',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.penalite || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-penalitesCession),
                isCessionSection: true
            } : null,

            // ======== INVERSÉ comme le backend ========

            // DIFF. SOLDE CESSION FAVEUR POOL → utilise calculatedSolde > 0
            {
                key: 'retro-diff-pool',
                description: 'DIFF. SOLDE CESSION FAVEUR POOL',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const cs = getCalculatedSolde(c);
                    acc[`compagnie${index}`] = cs > 0 ? formatNumberForExport(cs) : '';
                    return acc;
                }, {}),
                total: formatNumberForExport(
                    Math.round(compagniesArray?.reduce((sum, c) => {
                        const cs = getCalculatedSolde(c);
                        return sum + (cs > 0 ? cs : 0);
                    }, 0) || 0)
                ),
                isRetroSection: true
            },
            // DIFF. SOLDE CESSION FAVEUR COMPAGNIE → utilise calculatedSolde < 0
            {
                key: 'retro-diff-compagnie',
                description: 'DIFF. SOLDE CESSION FAVEUR COMPAGNIE',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const cs = getCalculatedSolde(c);
                    acc[`compagnie${index}`] = cs < 0 ? formatNumberForExport(Math.abs(cs)) : '';
                    return acc;
                }, {}),
                total: formatNumberForExport(
                    Math.round(compagniesArray?.reduce((sum, c) => {
                        const cs = getCalculatedSolde(c);
                        return sum + (cs < 0 ? Math.abs(cs) : 0);
                    }, 0) || 0)
                ),
                isRetroSection: true
            },
            // SOLDE DE RETROCESSION → utilise diffSoldeCession (inversé)
            {
                key: 'retro-solde',
                description: 'Solde de Retrocession ---->',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const diff = Math.round(c.diffSoldeCession || 0);
                    acc[`compagnie${index}`] = formatNumberForExport(Math.abs(diff));
                    return acc;
                }, {}),
                total: formatNumberForExport(Math.abs(totalDiffSoldeCession)),
                isSolde: true,
                isRetroSection: true
            }
        ].filter(Boolean);

        return { dataSource, compagniesArray };
    };

    // ============================================================
    // Colonnes du tableau (réutilisable)
    // ============================================================
    const getColumns = (compagniesArray) => [
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            fixed: 'left',
            width: 300,
            render: (text, record) => (
                <span style={{
                    fontWeight: record.isHeader || record.isSolde ? 'bold' : 'normal',
                    fontSize: record.isHeader ? '14px' : '12px',
                    color: record.isHeader ? '#722ed1' : 'inherit'
                }}>
                    {text}
                </span>
            )
        },
        ...(compagniesArray ?? []).map((c, index) => ({
            title: c.compagnie.raisonSociale.toUpperCase(),
            dataIndex: `compagnie${index}`,
            key: `compagnie${index}`,
            align: 'right',
            width: 150,
            render: (text, record) => (
                <span style={{
                    fontWeight: record.isSolde ? 'bold' : 'normal',
                    textDecoration: record.isSolde ? 'underline' : 'none'
                }}>
                    {text || ''}
                </span>
            )
        })),
        {
            title: 'TOTAL',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            width: 150,
            fixed: 'right',
            render: (text, record) => (
                <span style={{
                    fontWeight: record.isSolde || record.isHeader ? 'bold' : 'normal',
                    textDecoration: record.isSolde ? 'underline' : 'none',
                    fontSize: record.isHeader ? '14px' : '12px'
                }}>
                    {text || ''}
                </span>
            )
        }
    ];

    // ============================================================
    // Export Excel
    // ============================================================
    const exportToExcel = (selectedGroup) => {
        const { dataSource, compagniesArray } = createDataSource(selectedGroup);

        const excelData = [];

        const titre = `RETRO TOUS EXERCICES ${selectedGroup.branche?.codeBranche} ${frenchMonth(selectedGroup?.moisRapport?.split('-')[1])} ${selectedGroup?.moisRapport?.split('-')[0]}`;
        const dateEdition = `Édité le ${dayjs().format('DD MMMM YYYY')} par ${LOGGED_USER.personne?.nom} ${LOGGED_USER.personne?.prenom}`;

        excelData.push([titre]);
        excelData.push([dateEdition]);
        excelData.push([]);

        const headers = ['DESCRIPTION'];
        compagniesArray?.forEach(c => {
            headers.push(c.compagnie.raisonSociale.toUpperCase());
        });
        headers.push('TOTAL');
        excelData.push(headers);

        dataSource.forEach(row => {
            if (row) {
                const rowData = [row.description];
                compagniesArray?.forEach((c, index) => {
                    rowData.push(row[`compagnie${index}`] || '');
                });
                rowData.push(row.total || '');
                excelData.push(rowData);
            }
        });

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Récapitulatif');

        const fileName = `RETRO_${selectedGroup.branche?.codeBranche}_${selectedGroup?.moisRapport}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // ============================================================
    // Export PDF
    // ============================================================
    const exportToPDF = async (selectedGroup) => {
        const { dataSource, compagniesArray } = createDataSource(selectedGroup);

        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        let headerImage;
        try {
            headerImage = await loadImageAsBase64(entete);
        } catch (error) {
            console.error("Erreur chargement header:", error);
        }

        const insurances = await fetchInsuranceLogos();

        const addHeader = () => {
            if (headerImage) {
                doc.addImage(headerImage, 'PNG', 0, 0, pageWidth, 30);
            }
        };

        const addFooter = (pageNum) => {
            const footerY = pageHeight - 35;

            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(10, footerY, pageWidth - 10, footerY);

            doc.setFontSize(7);
            doc.setTextColor(30, 58, 138);
            const contactInfo = 'Tél: +228 22-20-55-77 / +228 22-20-55-78 | Whatsapp: +228 70-48-11-11 | Télécopie: +228 22-20-55-80 | N°RC: 2009 C 0771 - N°COE: 1000 16 1028';
            doc.text(contactInfo, pageWidth / 2, footerY + 5, { align: 'center' });

            if (insurances.length > 0) {
                const logoWidth = 15;
                const logoHeight = 10;
                const spacing = 40;
                const totalWidth = insurances.length * (logoWidth + spacing) - spacing;
                let startX = (pageWidth - totalWidth) / 2;

                insurances.forEach((insurance, index) => {
                    try {
                        const logoData = `data:image/png;base64,${insurance.logoDisplay}`;
                        doc.addImage(logoData, 'PNG', startX, footerY + 8, logoWidth, logoHeight);
                        startX += logoWidth + spacing;
                    } catch (error) {
                        console.error(`Erreur ajout logo ${index}:`, error);
                    }
                });
            }

            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('Entreprises régies par le code des assurances', pageWidth / 2, footerY + 20, { align: 'center' });

            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.text(`Page ${pageNum}`, pageWidth - 15, footerY + 25, { align: 'right' });
        };

        addHeader();

        const titre = `RETRO TOUS EXERCICES ${selectedGroup.branche?.codeBranche} ${frenchMonth(selectedGroup?.moisRapport?.split('-')[1])} ${selectedGroup?.moisRapport?.split('-')[0]}`;
        const dateEdition = `Édité le ${dayjs().format('DD MMMM YYYY')} par ${LOGGED_USER?.personne?.nom} ${LOGGED_USER?.personne?.prenom}`;

        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(titre, pageWidth / 2, 40, { align: 'center' });

        doc.setFontSize(10);
        doc.setTextColor(102, 102, 102);
        doc.text(dateEdition, pageWidth / 2, 47, { align: 'center' });

        const headers = ['DESCRIPTION'];
        compagniesArray?.forEach(c => {
            headers.push(c.compagnie.raisonSociale.toUpperCase());
        });
        headers.push('TOTAL');

        const body = [];
        dataSource.forEach(row => {
            if (row) {
                const rowData = [row.description];
                compagniesArray?.forEach((c, index) => {
                    rowData.push(row[`compagnie${index}`] || '');
                });
                rowData.push(row.total || '');
                body.push(rowData);
            }
        });

        autoTable(doc, {
            head: [headers],
            body: body,
            startY: 52,
            margin: { top: 35, bottom: 40, left: 10, right: 10 },
            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                lineColor: [200, 200, 200],
                lineWidth: 0.1
            },
            headStyles: {
                fillColor: [36, 33, 102],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 60, fontStyle: 'normal' }
            },
            didParseCell: function (data) {
                if (data.section === 'body') {
                    const row = dataSource[data.row.index];
                    if (row) {
                        if (row.isHeader) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [240, 240, 240];
                            data.cell.styles.textColor = [36, 33, 102];
                        }
                        if (row.isSolde) {
                            data.cell.styles.halign = 'right';
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fontSize = 8;
                        }
                        if (data.column.index > 0) {
                            data.cell.styles.halign = 'right';
                        }
                    }
                }
            },
            didDrawPage: function (data) {
                addFooter(doc.internal.getCurrentPageInfo().pageNumber);
                if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
                    addHeader();
                }
            }
        });

        const fileName = `RETRO_${selectedGroup.branche?.codeBranche}_${selectedGroup?.moisRapport}.pdf`;
        doc.save(fileName);
    };

    // ============================================================
    // Rendu du tableau — réutilise createDataSource
    // ============================================================
    const renderTableauRecapitulatif = (group) => {
        const { dataSource, compagniesArray } = createDataSource(group);
        const columns = getColumns(compagniesArray);

        return (
            <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1500 }}
                rowClassName={(record) => {
                    if (record.isHeader) return 'table-header-row';
                    if (record.isSolde) return 'table-solde-row';
                    return record.isCessionSection ? 'table-cession-row' : 'table-retro-row';
                }}
            />
        );
    };

    return (
        <div>
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <h3>
                    RETRO TOUS EXERCICES {selectedGroup.branche?.codeBranche}{' '}
                    {frenchMonth(selectedGroup?.mois?.split('-')[1])}{' '}
                    {selectedGroup?.mois?.split('-')[0]}
                </h3>
                <p style={{ fontSize: '12px', color: '#666' }}>
                    Édité le {dayjs().format('DD MMMM YYYY')} par {LOGGED_USER.nom}{' '}
                    {LOGGED_USER.prenom}
                </p>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button
                        onClick={() => exportToExcel(selectedGroup)}
                        style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', backgroundColor: '#21b317', color: '#fff', borderRadius: '4px' }}
                    >
                        📊 Exporter en Excel
                    </button>
                    <button
                        onClick={() => exportToPDF(selectedGroup)}
                        style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', backgroundColor: '#722ed1', color: '#fff', borderRadius: '4px' }}
                    >
                        📄 Exporter en PDF
                    </button>
                </div>
            </div>
            {renderTableauRecapitulatif(selectedGroup)}
        </div>
    )
}

export default TableauRecapitulatifRetrocession